import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { BotCard } from '@/components/admin/trading-analysis/BotCard';
import { BotStatistics } from '@/services/BotStatisticsService';
import { useDebounce } from '@/hooks/use-debounce';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
}

interface Filters {
  botId: string | null;
  instrument: string | null;
  dateFrom: string;
  dateTo: string;
}

interface LiveMonitorTabProps {
  filters: Filters;
  refreshKey: number;
  bots: Bot[];
}

interface BotStats {
  bot_id: string;
  bot_name: string;
  total_trades: number;
  correct_predictions: number;
  skip_predictions: number;
  accuracy_rate: number;
  last_exit_time: string | null;
}

interface BotStatsState {
  statsById: Record<string, BotStats>;
  botOrder: string[];
}

export function LiveMonitorTab({ filters, refreshKey, bots }: LiveMonitorTabProps) {
  const [botStatsState, setBotStatsState] = useState<BotStatsState>({
    statsById: {},
    botOrder: []
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isClearingEvents, setIsClearingEvents] = useState(false);

  // Debounce refresh key to coalesce rapid updates
  const debouncedRefreshKey = useDebounce(refreshKey, 150);

  // Initial data fetch
  const fetchInitialBotStats = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      let query = supabase
        .from('bot_signal_events')
        .select(`
          bot_id,
          action_type,
          win,
          timestamp,
          bots!bot_id(name, signal_token)
        `)
        .eq('action_type', 'EXIT');

      // Apply filters
      if (filters.botId) {
        query = query.eq('bot_id', filters.botId);
      }

      if (filters.instrument) {
        query = query.ilike('instrument', `%${filters.instrument}%`);
      }

      // Date filtering
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        query = query.gte('timestamp', fromDate.toISOString());
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('timestamp', toDate.toISOString());
      }

      const { data: signalEvents, error } = await query.order('timestamp', { ascending: false }).limit(10000);

      if (error) {
        console.error('Error fetching bot signal events:', error);
        return;
      }

      // Group and calculate stats for each bot
      const statsById: Record<string, BotStats> = {};
      
      signalEvents?.forEach((event: any) => {
        const botId = event.bot_id;
        const botName = event.bots?.name || `Bot ${botId.substring(0, 8)}`;
        
        if (!statsById[botId]) {
          statsById[botId] = {
            bot_id: botId,
            bot_name: botName,
            total_trades: 0,
            correct_predictions: 0,
            skip_predictions: 0,
            accuracy_rate: 0,
            last_exit_time: null,
          };
        }

        const stats = statsById[botId];
        stats.total_trades++;
        
        if (event.win === true) {
          stats.correct_predictions++;
        } else if (event.win === null) {
          stats.skip_predictions++;
        }
        
        // Update last exit time (events are ordered by timestamp desc)
        if (!stats.last_exit_time || event.timestamp > stats.last_exit_time) {
          stats.last_exit_time = event.timestamp;
        }
      });

      // Calculate accuracy rates
      Object.values(statsById).forEach(stats => {
        const nonSkipTrades = stats.total_trades - stats.skip_predictions;
        stats.accuracy_rate = nonSkipTrades > 0 
          ? (stats.correct_predictions / nonSkipTrades) * 100 
          : 0;
      });

      // Create stable bot order (sorted by name for consistency)
      const botOrder = Object.keys(statsById).sort((a, b) => {
        const nameA = statsById[a].bot_name;
        const nameB = statsById[b].bot_name;
        return nameA.localeCompare(nameB);
      });

      setBotStatsState({ statsById, botOrder });
    } catch (error) {
      console.error('Error in fetchInitialBotStats:', error);
    } finally {
      setIsInitialLoading(false);
    }
  }, [filters]);

  // Clear bot signal events
  const clearBotEvents = useCallback(async () => {
    if (!filters.botId) {
      toast.error('Please select a specific bot to clear events');
      return;
    }

    try {
      setIsClearingEvents(true);
      
      // Build delete query with current filters
      let query = supabase
        .from('bot_signal_events')
        .delete()
        .eq('bot_id', filters.botId);

      // Apply date filters if present
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        query = query.gte('timestamp', fromDate.toISOString());
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('timestamp', toDate.toISOString());
      }

      const { error, count } = await query;

      if (error) {
        console.error('Error clearing events:', error);
        toast.error('Failed to clear events');
        return;
      }

      toast.success(`Cleared ${count || 0} signal events`);
      
      // Refresh the data after deletion
      await fetchInitialBotStats();
    } catch (error) {
      console.error('Error clearing events:', error);
      toast.error('Failed to clear events');
    } finally {
      setIsClearingEvents(false);
    }
  }, [filters, fetchInitialBotStats]);

  // Update single bot stats incrementally
  const updateBotStatsIncremental = useCallback((botId: string, win: boolean | null, exitTime: string) => {
    setBotStatsState(prevState => {
      const { statsById, botOrder } = prevState;
      
      // Get current stats or create new entry
      const currentStats = statsById[botId];
      if (!currentStats) {
        // If bot doesn't exist in current state, we need to refetch
        // This happens when a new bot appears that wasn't in initial filter
        return prevState;
      }

      // Create updated stats (new object reference for React optimization)
      const updatedStats: BotStats = {
        ...currentStats,
        total_trades: currentStats.total_trades + 1,
        correct_predictions: win === true ? currentStats.correct_predictions + 1 : currentStats.correct_predictions,
        skip_predictions: win === null ? currentStats.skip_predictions + 1 : currentStats.skip_predictions,
        last_exit_time: exitTime
      };

      // Recalculate accuracy
      const nonSkipTrades = updatedStats.total_trades - updatedStats.skip_predictions;
      updatedStats.accuracy_rate = nonSkipTrades > 0 
        ? (updatedStats.correct_predictions / nonSkipTrades) * 100 
        : 0;

      return {
        statsById: {
          ...statsById,
          [botId]: updatedStats
        },
        botOrder // Keep order stable
      };
    });
  }, []);

  // Handle filter changes and initial load  
  useEffect(() => {
    fetchInitialBotStats();
  }, [filters, fetchInitialBotStats]);

  // Setup Realtime subscription for bot_signal_events
  useEffect(() => {
    const channel = supabase
      .channel('bot-signal-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bot_signal_events',
          filter: 'action_type=eq.EXIT'
        },
        (payload: RealtimePostgresChangesPayload<any>) => {
          const newEvent = payload.new;
          
          // Check if event matches current filters
          let matches = true;
          
          if (filters.botId && newEvent.bot_id !== filters.botId) {
            matches = false;
          }
          
          if (filters.instrument && !newEvent.instrument?.toLowerCase().includes(filters.instrument.toLowerCase())) {
            matches = false;
          }
          
          if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (new Date(newEvent.timestamp) < fromDate) {
              matches = false;
            }
          }
          
          if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (new Date(newEvent.timestamp) > toDate) {
              matches = false;
            }
          }
          
          if (matches) {
            setIsRefreshing(true);
            updateBotStatsIncremental(newEvent.bot_id, newEvent.win, newEvent.timestamp);
            setTimeout(() => setIsRefreshing(false), 300); // Brief refresh indicator
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [filters, updateBotStatsIncremental]);

  if (isInitialLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Live Monitor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (botStatsState.botOrder.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Live Monitor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              No bot data found for the selected filters.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Only bots with EXIT trades are shown.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Live Monitor</CardTitle>
          <div className="flex items-center gap-3">
            {filters.botId && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearBotEvents}
                disabled={isClearingEvents}
                className="text-destructive hover:text-destructive"
              >
                {isClearingEvents ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                {isClearingEvents ? 'Clearing...' : 'Clear Events'}
              </Button>
            )}
            {isRefreshing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Updating...</span>
              </div>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Real-time bot performance monitoring based on EXIT signals
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
          {botStatsState.botOrder.map((botId) => {
            const stats = botStatsState.statsById[botId];
            if (!stats) return null;

            const botStatistics: BotStatistics = {
              current_prediction: 'WIN', // This would need to come from another source
              accuracy_rate: stats.accuracy_rate,
              correct_predictions: stats.correct_predictions,
              total_predictions: stats.total_trades,
              skip_predictions: stats.skip_predictions,
              loss_predictions: stats.total_trades - stats.correct_predictions - stats.skip_predictions,
              win_predictions: stats.correct_predictions,
              last_prediction_at: stats.last_exit_time || new Date().toISOString(),
            };

            return (
              <BotCard
                key={stats.bot_id} // Stable key prevents remounting
                id={stats.bot_id}
                name={stats.bot_name}
                statistics={botStatistics}
                onClick={() => {
                  console.log('Bot clicked:', stats.bot_id);
                }}
                className="cursor-pointer hover:shadow-md transition-all duration-300 hover:scale-[1.02]"
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}